import { ChangeEvent, PropsWithChildren } from 'react'

import { useTheme } from '@chakra-ui/react'
import { CheckIcon } from '@verbit-ai/icons-library'
import { Drawer, Popover } from '@verbit-ai/verbit-ui-library'
import { UseDisclosureReturn } from '@chakra-ui/react'

import { useConfig } from 'src/contexts/ConfigurationContext'
import { useResponsiveContainer } from 'src/contexts/ResponsiveContainerContext'
import { FONT_SIZE_PERCENTAGE_OPTIONS } from 'src/models/UIConfiguration'

import * as S from './styles'

type FontSizeControlProps = PropsWithChildren & Pick<UseDisclosureReturn, 'isOpen' | 'onOpen' | 'onClose'>

export const FONT_SIZE_CONTROL_CLASSNAME = 'font-size-control'

export const FontSizeControl = (props: FontSizeControlProps) => {
    const { isOpen, onOpen, onClose, children } = props
    const [config, setProp] = useConfig()
    const { isMediumContainer, containerRef } = useResponsiveContainer()
    const {
        palette: {
            fontSizeControl: { iconColor },
        },
    } = useTheme()

    const toggleContainerFullWidth = (event: ChangeEvent<HTMLInputElement>) => {
        setProp('isFullWidthMode', event.target.checked)
        onClose()
    }

    const fontSizePanel = () => (
        <S.FontSizePanel>
            <S.FontSizeOptionList role="list">
                {FONT_SIZE_PERCENTAGE_OPTIONS.map((fontSizePercentage) => {
                    const isSelected = fontSizePercentage === config.fontSizePercentage

                    return (
                        <S.FontSizeOptionItem key={fontSizePercentage}>
                            <S.FontSizeOption
                                tabIndex={0}
                                aria-label={`Set font size to ${fontSizePercentage}%`}
                                aria-pressed={isSelected}
                                onClick={() => setProp('fontSizePercentage', fontSizePercentage)}>
                                <span>{fontSizePercentage}%</span>
                                {isSelected && <CheckIcon size={16} color={iconColor} aria-hidden />}
                            </S.FontSizeOption>
                        </S.FontSizeOptionItem>
                    )
                })}
            </S.FontSizeOptionList>
            <S.FullWidthToggleWrapper>
                <S.StyledCheckbox defaultChecked={config.isFullWidthMode} onChange={toggleContainerFullWidth}>
                    Expand to full width view
                </S.StyledCheckbox>
            </S.FullWidthToggleWrapper>
        </S.FontSizePanel>
    )

    return isMediumContainer ? (
        <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" autoFocus={false} portalProps={{ containerRef }}>
            {fontSizePanel()}
        </Drawer>
    ) : (
        <Popover
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            triggerEl={children}
            trigger="click"
            placement="bottom-end"
            boxShadow="verbit_floating_action_button_shadow"
            contentWidth="unset">
            {fontSizePanel()}
        </Popover>
    )
}
